import { fetcherModeAtom } from "@keepeek/commons";
import trim from "lodash/trim";
import { selector, selectorFamily } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { WidgetConfigurations } from "../../../containers/widgets/models";
import { getWidgetConfigurationsByTemplates } from "../../../containers/widgets/utils/getWidgetConfigurations";
import { getAdminConfigSectionData } from "../../../lib/config/customer-config-utils";
import logger from "../../../lib/logger-utils";
import { Basket } from "../../../models/configuration/components/basket";
import { BusinessFilter } from "../../../models/configuration/components/businessFilter";
import { Dataview } from "../../../models/configuration/components/dataview";
import { DownloadManager } from "../../../models/configuration/components/downloadManager";
import { FolderBrowse } from "../../../models/configuration/components/folderBrowse";
import { ShareManager } from "../../../models/configuration/components/shareManager";
import { Template } from "../../../models/configuration/definitions/template";
import { Global, PermanentFilter } from "../../../models/configuration/global";
import { Element } from "../../../models/configuration/pages/element";
import { Search } from "../../../models/configuration/pages/search";
import { Key, Theme } from "../../../models/configuration/theme";
import { configSectionFetcherSelector } from "../../fetcher/selectors/configSection";
import { isTemplatesConfiguredWithWidget, KTemplatesTemplatesName } from "../utils";
import { jsonConfigSelector } from "./init";

/**
  @return undefined when the data is not yet loaded otherwise return data when some data or null
 */
export const configSectionDataByPathSelector = selectorFamily<
  any | undefined,
  CONFIGURATION_SECTION_PATH
>({
  key: "ConfigSectionByNameSelector",
  get:
    (name) =>
    ({ get }) => {
      const jsonConfig = get(jsonConfigSelector);
      if (jsonConfig === undefined) {
        // data not yet loaded => return undefined
        return undefined;
      }
      return getAdminConfigSectionData(jsonConfig, name);
    },
});

export const configSectionComponentDataViewSelector = selector<Dataview | undefined>({
  key: "ConfigSectionDataViewSelector",
  get: ({ get }) =>
    get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.COMPONENTS_DATAVIEW)),
});

export const configSectionGlobalSelector = selector<Global | undefined>({
  key: "ConfigSectionGlobalSelector",
  get: ({ get }) => get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.GLOBAL)),
});

export const configSectionShareManagerSelector = selector<ShareManager | undefined>({
  key: "ConfigSectionShareManagerSelector",
  get: ({ get }) =>
    get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.COMPONENTS_SHARE_MANAGER)),
});

export const configSectionFolderBrowseSelector = selector<FolderBrowse | undefined>({
  key: "ConfigSectionFolderBrowseSelector",
  get: ({ get }) =>
    get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.COMPONENTS_FOLDER_BROWSE)),
});

export const configSectionThemeSelector = selector<Theme | undefined>({
  key: "ConfigSectionThemeSelector",
  get: ({ get }) => get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.THEME)),
});

export const configSectionComponentDownloadManagerSelector = selector<DownloadManager | undefined>({
  key: "ConfigSectionComponentDownloadManagerSelector",
  get: ({ get }) =>
    get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.COMPONENTS_DOWNLOAD_MANAGER)),
});

export const configSectionComponentBasketSelector = selector<Basket | undefined>({
  key: "ConfigSectionComponentBasketSelector",
  get: ({ get }) =>
    get(configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.COMPONENTS_BASKET)),
});

export const initCustomerConfigSectionPageSearch: Search = {
  sort: {},
  functionalFieldsToDisplay: {},
  technicalFieldsToDisplay: {},
  visualAlerts: [],
};

export const configSectionPageSearchSelector = selector<Search | undefined>({
  key: "ConfigSectionPageSearchSelector",
  get: ({ get }) => {
    const { pageSearchSection } = get(configSectionFetcherSelector(get(fetcherModeAtom)));

    const searchPageConfig = configSectionDataByPathSelector(pageSearchSection);
    if (!searchPageConfig) {
      return initCustomerConfigSectionPageSearch;
    }
    return get(searchPageConfig);
  },
});

export const configSectionPageElementSelector = selector<Element | undefined>({
  key: "ConfigSectionPageElementSelector",
  get: ({ get }) => {
    const { pageElementSection } = get(configSectionFetcherSelector(get(fetcherModeAtom)));

    return get(configSectionDataByPathSelector(pageElementSection));
  },
});

/**
 * @return null when in loading state otherwise PermanentFilter[]
 */
export const configSectionGlobalPermanentFiltersSelector = selector<PermanentFilter[] | null>({
  key: "ConfigSectionGlobalPermanentFiltersSelector",
  get: ({ get }) => {
    const conf = get(configSectionGlobalSelector);
    if (conf === undefined) {
      // loading state
      return null;
    }
    return conf?.permanentFilters ?? [];
  },
});

/**
 * @return null when in loading state otherwise a BusinessFilter
 */
export const configSectionComponentBusinessFilterSelector = selector<BusinessFilter | null>({
  key: "ConfigSectionComponentBusinessFilterSelector",
  get: ({ get }) => {
    const conf = get(
      configSectionDataByPathSelector(CONFIGURATION_SECTION_PATH.COMPONENTS_BUSINESS_FILTER),
    );
    if (conf === undefined) {
      // loading state
      return null;
    }
    return (
      conf || {
        filters: [],
      }
    );
  },
});

// Deprecated selector
export const configSectionThemeAssetSelector = selectorFamily<string[], Key>({
  key: "ConfigSectionThemeAssetImageSelector",
  get:
    (assetKey: Key) =>
    ({ get }) => {
      const configSectionTheme = get(configSectionThemeSelector);
      const asset = configSectionTheme?.assets?.find((a) => a.key === assetKey);

      if (asset && asset.values && asset.values.length > 0 && trim(asset.values[0]) !== "") {
        return asset.values;
      }
      return [""];
    },
});

export const configSectionKTemplateByPosition = selectorFamily<
  { templates: Template[] | undefined; widgetsConfiguration: WidgetConfigurations | undefined },
  {
    // add locale deps in order to retrieve the configuration of widgets coming from the API with the correct language when local change
    locale: string | undefined;
    templatePagePosition?: CONFIGURATION_SECTION_PATH;
    templatesName?: KTemplatesTemplatesName;
    // attention, this string must only contain a stringified version of the TemplateSchema [] object !
    defaultTemplatesStringify?: string;
  }
>({
  key: "ConfigSectionKTemplateByPosition",
  get:
    ({
      templatePagePosition,
      templatesName = KTemplatesTemplatesName.Templates,
      defaultTemplatesStringify = "",
    }) =>
    ({ get }) => {
      const configSections = get(jsonConfigSelector);

      if (!configSections) {
        return { templates: undefined, widgetsConfiguration: undefined };
      }
      const configSection = getAdminConfigSectionData<Template>(
        configSections,
        templatePagePosition,
      );

      let templates = configSection?.[templatesName];

      // Get default template if there is none configured :
      if (!isTemplatesConfiguredWithWidget(templates) && defaultTemplatesStringify) {
        try {
          templates = JSON.parse(defaultTemplatesStringify);
        } catch (error) {
          logger.error("error during parse defaultTemplatesStringify", error);
        }
      }

      const widgetsConfiguration = getWidgetConfigurationsByTemplates({
        configSections,
        templates,
      });

      return {
        templates,
        widgetsConfiguration,
      };
    },
});
